/**
 * Polkadot APIインスタンスを生成する関数を定義するファイル
 */

import { ApiPromise, WsProvider } from '@polkadot/api'

/**
 * Polkadot APIインスタンス生成関数の型
 * @param args - 接続先のWebSocket URLを含むオブジェクト
 * @returns ApiPromiseインスタンスをPromiseで返す
 */
type CreateApiInstance = (args: {
  /**
   * PolkadotノードのWebSocketエンドポイントURL
   */
  url: string
}) => Promise<ApiPromise>

/**
 * Polkadotノードに接続し、ApiPromiseインスタンスを生成する関数
 * @param args - WebSocketエンドポイントURLを含むオブジェクト
 * @returns 初期化済みのApiPromiseインスタンス
 */
export const createApiInstance: CreateApiInstance = async ({ url }) => {
  // WebSocketプロバイダーを作成
  const provider = new WsProvider(url, false)

  let api: ApiPromise | undefined
  try {
    api = await Promise.race([
      (async () => {
        await provider.connect()
        api = await ApiPromise.create({
          provider,
          noInitWarn: true,
          throwOnConnect: true,
          throwOnUnknown: true,
        })
        await api.isReady
        return api
      })(),
      new Promise<never>((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(
                'Timeout: ApiPromise initialization exceeded 60 seconds',
              ),
            ),
          60_000,
        ),
      ),
    ])
    return api
  } catch (err) {
    // タイムアウト時や初期化失敗時はプロバイダーを切断
    if (api) {
      await api.disconnect()
    }
    await provider.disconnect()
    throw err
  }
}
